import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { actionPlanKeys, fetchActionPlanSearchV2 } from '@/core/api/queries';
import ObligationService from '@/shared/services/ObligationService';
import { collectObligationIdsFromNotifications } from './obligationNotificationLinks';
import { MODERATOR_ASSIGNMENT_CHANGED_EVENT } from './moderatorAssignmentEvents';

/**
 * Učitava metapodatke obaveza (action_plan_id, naziv) za notifikacije u header dropdown-u.
 * BE /notification/search vraća samo related_entity_id — AP id se dohvata pretragom obaveza.
 *
 * @param {{ enabled?: boolean, notifications?: object[] }} [options]
 * @param {boolean} [options.enabled] - Da li je hook aktivan.
 * @param {object[]} [options.notifications] - Lista obaveštenja sa obligation related_entity_id.
 * @returns {{ obligations: object[], obligationsInPreparation: object[], isLoading: boolean }}
 */
export function useNotificationObligations({ enabled = false, notifications = [] } = {}) {
  const obligationIds = useMemo(
    () => collectObligationIdsFromNotifications(notifications),
    [notifications],
  );
  const idsKey = obligationIds.join(',');
  const [obligations, setObligations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncGeneration, setSyncGeneration] = useState(0);
  const queryClient = useQueryClient();

  useEffect(() => {
    const onModeratorAssignmentChanged = () => {
      setSyncGeneration((value) => value + 1);
    };
    window.addEventListener(MODERATOR_ASSIGNMENT_CHANGED_EVENT, onModeratorAssignmentChanged);
    return () => {
      window.removeEventListener(MODERATOR_ASSIGNMENT_CHANGED_EVENT, onModeratorAssignmentChanged);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !obligationIds.length) {
      setObligations([]);
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setObligations([]);
    setIsLoading(true);

    (async () => {
      try {
        const apData = await queryClient.fetchQuery({
          queryKey: actionPlanKeys.searchV2(),
          queryFn: fetchActionPlanSearchV2,
        });
        const aps = [
          apData?.draft,
          apData?.implementation,
          ...(Array.isArray(apData?.completed) ? apData.completed : []),
        ].filter(Boolean);
        const found = [];

        await Promise.all(
          obligationIds.map(async (obligationId) => {
            for (const ap of aps) {
              try {
                const res = await ObligationService.search({
                  action_plan_id: ap.id,
                  id: obligationId,
                });
                const obl = (res?.obligations ?? []).find(
                  (row) => String(row?.id) === String(obligationId),
                );
                if (!obl) continue;
                found.push({
                  ...obl,
                  _apId: ap.id,
                  _apStatus: ap.status ?? null,
                });
                return;
              } catch {
                /* AP može biti nedostupan za ulogu */
              }
            }
          }),
        );

        if (!cancelled) setObligations(found);
      } catch {
        if (!cancelled) setObligations([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, idsKey, obligationIds, queryClient, syncGeneration]);

  const obligationsInPreparation = useMemo(
    () => obligations.filter((o) => String(o?._apStatus ?? '').toUpperCase() === 'DRAFT'),
    [obligations],
  );

  return {
    obligations,
    obligationsInPreparation,
    isLoading,
  };
}
